// src/components/apps/SUA/SuaGenerator.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  TextField,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import api from "../../../utils/api"; // ✅ Import your API utility

// ===== Constants =====
const REQUIRED_COLUMNS = ["fbcompany", "fvendno", "fcinvoice", "fnamount"];

const PAYMENT_GROUPS = {
  "CT - Rockmart - VC": "3110",
  "CT - San Diego - VC": "3140",
  "CT - Baltimore - VC": "3150",
  "CT - Erl-Cin - VC": "3170",
};

// >>> Timestamp helper for filename: YYYYMMDDHHMM00000 (17 chars)
function suaTimestamp() {
  const now = new Date();
  const pad = (n, l = 2) => String(n).padStart(l, "0");
  const datePart =
    now.getFullYear().toString() +
    pad(now.getMonth() + 1) +
    pad(now.getDate()) +
    pad(now.getHours()) +
    pad(now.getMinutes());
  return `${datePart}00000`;
}

// ===== Component =====
const SuaGenerator = () => {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("Per Vendor");
  const [groupLabel, setGroupLabel] = useState("CT - San Diego - VC");
  const [confirm, setConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState(null);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const [preview, setPreview] = useState("");
  const [txtContent, setTxtContent] = useState("");
  const [csvContent, setCsvContent] = useState("");
  const [generatedCount, setGeneratedCount] = useState(0);
  const [vendorsValidated, setVendorsValidated] = useState(0);

  const PAYMENT_GROUP_ID = PAYMENT_GROUPS[groupLabel];

  const resetOutputs = () => {
    setWarning(null);
    setError(null);
    setValidationErrors([]);
    setPreview("");
    setTxtContent("");
    setCsvContent("");
    setGeneratedCount(0);
    setVendorsValidated(0);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    resetOutputs();
    if (selected) {
      console.log("📂 File selected:", selected.name, selected.size);
    }
  };

  const canGenerate = !!file && !!confirm && !loading;

  const download = (content, filename, mime) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleGenerate = async () => {
    setError(null);
    setWarning(null);
    setValidationErrors([]);
    setPreview("");
    setTxtContent("");
    setCsvContent("");
    setGeneratedCount(0);
    setVendorsValidated(0);

    if (!file) return setError("Please choose a file (CSV or Excel).");
    if (!confirm) return setError("Please confirm the selected Payment Group.");

    try {
      setLoading(true);

      // ✅ Create FormData for file upload
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mode", mode);
      formData.append("payment_group_label", groupLabel);
      formData.append("confirm", "true");

      // ✅ Call the backend API
      const response = await api.post("/payments/sua/generate/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const data = response.data;

      // ✅ Handle response
      if (data.warning) {
        setWarning(data.warning);
      }

      setPreview(data.txt_content);
      setTxtContent(data.txt_content);
      setCsvContent(data.csv_content);
      setGeneratedCount(data.record?.record_count || 0);
      setVendorsValidated(data.vendors_validated || 0);

      console.log("✅ Generation successful:", data.record);
    } catch (e) {
      console.error("❌ Generation error:", e);

      // ✅ Handle validation errors from backend
      if (e.response?.data?.errors) {
        setValidationErrors(e.response.data.errors);
        setError(e.response.data.detail || "Vendor validation failed");
      } else if (e.response?.data?.detail) {
        setError(e.response.data.detail);
      } else {
        setError(e.message || "Error generating SUA batch");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="main" sx={{ mt: 4 }}>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          p: 3,
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          mt: "50px",
          mb: 4,
          textAlign: "center",
          transition: "transform 0.3s ease, boxShadow 0.3s ease",
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <Typography variant="h4" sx={{ mb: 2, color: "primary.main" }}>
          SUA Generator
        </Typography>

        {/* ✅ Quick Navigation */}
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
          <Button component={RouterLink} to="/vendors" variant="outlined" size="small">
            Manage Vendors
          </Button>
          <Button component={RouterLink} to="/payment-records" variant="outlined" size="small">
            View Payment Records
          </Button>
        </Stack>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Stack spacing={2}>
            {/* File input */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="outlined" component="label">
                {file ? "Change File" : "Select File"}
                <input 
                  type="file" 
                  hidden 
                  accept=".csv,.xls,.xlsx" 
                  onChange={handleFileChange} 
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                {file ? `${file.name} • ${(file.size / 1024).toFixed(1)} KB` : "No file selected"}
              </Typography>
            </Stack>

            {/* Mode */}
            <FormControl>
              <FormLabel>Payment Mode</FormLabel>
              <RadioGroup
                row
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                name="payment-mode"
              >
                <FormControlLabel value="Per Vendor" control={<Radio />} label="Per Vendor" />
                <FormControlLabel value="Per Invoice" control={<Radio />} label="Per Invoice" />
              </RadioGroup>
            </FormControl>

            {/* Group */}
            <FormControl fullWidth>
              <FormLabel>Payment Group</FormLabel>
              <Select
                value={groupLabel}
                onChange={(e) => setGroupLabel(e.target.value)}
                size="small"
              >
                {Object.keys(PAYMENT_GROUPS).map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Confirm */}
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
                }
                label={`Confirm use of selected Payment Group (${PAYMENT_GROUPS[groupLabel]})`}
              />
            </FormGroup>

            {/* File requirements hint */}
            <Alert severity="info">
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                File Format Support
              </Typography>
              <strong>CSV files:</strong> Columns must be <code>fbcompany, fvendno, fcinvoice, fnamount</code>
              <br />
              <strong>Excel files (AP Aging):</strong> Columns must be <code>Vendor, Vendor #, Invoice, Net Due</code>
              <br />
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                Optional: <code>fpayment_date</code> (ISO date). All vendors in <code>fvendno</code> or <code>Vendor #</code> must exist in the{" "}
                <Button
                  component={RouterLink}
                  to="/vendors"
                  size="small"
                  sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                >
                  vendor database
                </Button>
                .
              </Typography>
            </Alert>

            {/* Status messages */}
            {error && (
              <Alert severity="error">
                {error}
                {validationErrors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">Missing vendors:</Typography>
                    <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                      {validationErrors.map((err, idx) => (
                        <li key={idx}>
                          <strong>{err.vendor_id}</strong>: {err.message}
                        </li>
                      ))}
                    </ul>
                    <Button
                      component={RouterLink}
                      to="/vendors"
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    >
                      Add Missing Vendors
                    </Button>
                  </Box>
                )}
              </Alert>
            )}
            {warning && <Alert severity="warning">{warning}</Alert>}
            {generatedCount > 0 && (
              <Alert severity="success">
                Generated <strong>{generatedCount}</strong> payment record
                {generatedCount === 1 ? "" : "s"}.
                <br />
                Validated <strong>{vendorsValidated}</strong> vendor
                {vendorsValidated === 1 ? "" : "s"} from database.
              </Alert>
            )}

            {/* Actions */}
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerate}
                disabled={!canGenerate}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                {loading ? "Generating..." : "Generate"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Results */}
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Output</Typography>

            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Button
                variant="outlined"
                disabled={!txtContent}
                onClick={() =>
                  download(
                    txtContent,
                    `CARBTOPGTI.ORDER.BATCH1.${suaTimestamp()}.IN`,
                    "text/plain;charset=utf-8"
                  )
                }
              >
                Download SUA .IN
              </Button>

              <Button
                variant="outlined"
                disabled={!csvContent}
                onClick={() =>
                  download(
                    csvContent,
                    `CARBTOPGTI.ORDER.BATCH1.${suaTimestamp()}.csv`,
                    "text/csv;charset=utf-8"
                  )
                }
              >
                Download CSV
              </Button>

              <Button
                component={RouterLink}
                to="/payment-records"
                variant="outlined"
                disabled={!txtContent}
              >
                View in Records
              </Button>
            </Stack>

            <TextField
              label="Preview (TXT)"
              value={preview}
              InputProps={{ readOnly: true }}
              multiline
              minRows={12}
              sx={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                "& .MuiInputBase-input": { fontFamily: "inherit" },
              }}
            />
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default SuaGenerator;